import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, ShieldCheck, CheckCircle2, ArrowUpRight } from "lucide-react";

/**
 * Pending-Reservation banner on the buyer dashboard. Surfaces a warm "your
 * campaign is reserved — under 24h compliance review" pill until the admin
 * approves it. Once approved, the badge auto-flips to "Live" and remains
 * visible for 24h before fading.
 *
 * Sources: GET /api/me/reservations (returns user's own reservations).
 */
export default function ReservedCampaignBanner() {
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/me/reservations").then((r) => { if (!cancelled) setReservations(r.data || []); }).catch(() => {});
    load();
    const t = setInterval(load, 20000); // poll every 20s — cheap because list is tiny
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Surface the most recent reservation that's pending or just went live
  const r = reservations[0];
  if (!r) return null;

  const isPending = r.status === "pending_payment" || r.status === "deposit_paid";
  const isLive = r.status === "live";
  if (!isPending && !isLive) return null;

  const sector = (r.config?.sector || "").replace(/-/g, " ");
  const volume = r.config?.volume?.toLocaleString() || "—";
  const reservedAt = new Date(r.created_at);
  const hoursSince = Math.floor((Date.now() - reservedAt.getTime()) / 36e5);
  const hoursLeft = Math.max(0, 24 - hoursSince);

  return (
    <div
      className={`rounded-xl border bg-white p-4 sm:p-5 flex flex-wrap items-center gap-4 ${isLive ? "border-emerald-200" : "border-slate-200"}`}
      data-testid="reserved-campaign-banner"
    >
      <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white shrink-0">
        {isLive ? <CheckCircle2 className="size-5" strokeWidth={1.8} /> : (r.status === "deposit_paid" ? <ShieldCheck className="size-5" strokeWidth={1.6} /> : <Clock className="size-5" strokeWidth={1.6} />)}
      </span>

      <div className="flex-1 min-w-[240px] leading-tight">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 text-[15px]">
            {isLive ? "Your campaign is live" : "Campaign reserved · under compliance review"}
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${
              isLive
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : (r.status === "deposit_paid" ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-slate-100 text-slate-600")
            }`}
            data-testid="reserved-status-pill"
          >
            {isLive ? (<><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live</>) : (r.status === "deposit_paid" ? <>In review</> : <>Awaiting payment</>)}
          </span>
        </div>
        <div className="text-[12.5px] text-slate-600 mt-1">
          <span className="capitalize">{sector || "Campaign"}</span> · {volume} leads/mo
          {isPending && r.status === "deposit_paid" && (
            <> · <span className="font-mono text-slate-700">{hoursLeft}h</span> remaining in 24h review window</>
          )}
          {isLive && r.approved_at && (
            <> · approved {new Date(r.approved_at).toLocaleString()}</>
          )}
        </div>
      </div>

      <Link to={isLive ? "/dashboard/leads" : "/dashboard/billing"} className="inline-flex items-center gap-1 text-[12.5px] text-[#0055FF] hover:text-[#0044CC] font-medium shrink-0" data-testid="reserved-banner-cta">
        {isLive ? "View leads" : "View invoice"} <ArrowUpRight className="size-3" />
      </Link>
    </div>
  );
}
